'use client';

import { usePathname } from 'next/navigation';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useInterval from './useInterval';
import { MarketDetails24H } from '@velocity-exchange/common';
import DataApiClient from 'src/utils/DataApiClient';
import StatsApiClient from 'src/utils/StatsApiClient';

const REFRESH_RATE_SECS = 60 * 5;

type MarketsVolumeResponse = {
	success: boolean;
	total: number;
	markets: {
		baseVolume: string;
		marketIndex: number;
		marketType: string;
		quoteVolume: string;
		symbol: string;
	}[];
};

const use24hMarketDataFromApi = () => {
	const pathname = usePathname();
	const setState = useDriftStore((s) => s.set);

	// Only fetch 24h market data on trading and stats pages
	const enabled = pathname
		? needsDataRequirement(pathname, 'trading') ||
			needsDataRequirement(pathname, 'stats')
		: false;

	const isMarketsVolumeResponse = (
		data: unknown
	): data is MarketsVolumeResponse =>
		typeof data === 'object' &&
		data !== null &&
		'success' in data &&
		'markets' in data &&
		Array.isArray((data as MarketsVolumeResponse).markets);

	const pull24hMarketData = async () => {
		if (!enabled) return;

		try {
			const [marketSummary, volumeData24hRaw, volumeData30dRaw] =
				await Promise.all([
					StatsApiClient.fetchMarketSummary(),
					DataApiClient.fetchShortTermData('marketsVolume24h'),
					DataApiClient.fetchShortTermData('marketsVolume30d'),
				]);

			const volumeData24h: MarketsVolumeResponse | null =
				isMarketsVolumeResponse(volumeData24hRaw) ? volumeData24hRaw : null;
			const volumeData30d: MarketsVolumeResponse | null =
				isMarketsVolumeResponse(volumeData30dRaw) ? volumeData30dRaw : null;

			if (!marketSummary.success || !marketSummary.markets) {
				return;
			}

			// Build 30D volume lookup map for efficient access
			const volume30dMap = new Map<
				string,
				{ baseVolume: number; quoteVolume: number }
			>();
			if (volumeData30d?.success && volumeData30d.markets) {
				volumeData30d.markets.forEach((market) => {
					const key = `${market.marketType}-${market.marketIndex}`;
					volume30dMap.set(key, {
						baseVolume: parseFloat(market.baseVolume),
						quoteVolume: parseFloat(market.quoteVolume),
					});
				});
			}

			// Convert the new market summary format to MarketDetails24H
			const marketData: MarketDetails24H[] = marketSummary.markets.map(
				(market) => {
					const oraclePrice = parseFloat(market.oraclePrice);
					const priceChange24h = parseFloat(market.priceChange24h);
					// Calculate price24hAgo from current price minus the change
					const price24hAgo = oraclePrice - priceChange24h;

					// Get 30D volume from the map
					const volume30dKey = `${market.marketType}-${market.marketIndex}`;
					const volume30d = volume30dMap.get(volume30dKey);

					// Parse 24h average funding rate (only available for perp markets)
					const fundingRate24h = market.fundingRate24h
						? parseFloat(market.fundingRate24h)
						: undefined;

					return {
						marketIndex: market.marketIndex,
						marketType:
							market.marketType === 'perp' ? { perp: {} } : { spot: {} },
						symbol: market.symbol,
						oraclePrice,
						markPrice: parseFloat(market.markPrice ?? market.oraclePrice),
						baseVolume: parseFloat(market.baseVolume),
						quoteVolume: parseFloat(market.quoteVolume),
						baseVolume30D: volume30d?.baseVolume ?? 0,
						quoteVolume30D: volume30d?.quoteVolume ?? 0,
						price24hAgo,
						pricePercentChange: parseFloat(market.priceChange24hPercent),
						priceHigh: market.priceHigh
							? parseFloat(market.priceHigh.oracle)
							: 0,
						priceLow: market.priceLow ? parseFloat(market.priceLow.oracle) : 0,
						marketCap: 0, // Not available in new API
						dailyVolumeIncreaseZScore: 0, // Not available in new API
						// api only returns 24h average funding rate
						avgFunding: fundingRate24h,
						avgLongFunding: fundingRate24h,
						avgShortFunding: fundingRate24h,
					};
				}
			);

			if (volumeData24h?.success && volumeData24h.markets.length > 0) {
				// Stitch together new volume data with existing market data
				volumeData24h.markets.forEach((market) => {
					const marketIndex = market.marketIndex;
					const marketType = market.marketType;
					const quoteVolume = parseFloat(market.quoteVolume);
					const baseVolume = parseFloat(market.baseVolume);
					const symbol = market.symbol;

					const existingMarket = marketData.find((m) => {
						if (m.marketIndex !== marketIndex) return false;
						// Check if the marketType object has the expected key
						return (
							m.marketType &&
							typeof m.marketType === 'object' &&
							marketType in m.marketType
						);
					});

					if (existingMarket) {
						existingMarket.quoteVolume = quoteVolume;
						existingMarket.baseVolume = baseVolume;
						existingMarket.symbol = symbol;
					}
				});
			}

			set24hMarketData(marketData);
		} catch (error) {
			console.error('Error fetching 24h market data:', error);
		}
	};

	const set24hMarketData = (newData: MarketDetails24H[]) => {
		setState((s) => {
			s.marketsData24H = newData;
		});
	};

	useInterval(
		() => {
			pull24hMarketData();
		},
		enabled ? 1000 * REFRESH_RATE_SECS : null
	);
};

export default use24hMarketDataFromApi;
